// #2121-spec-kits
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, UncontrolledTooltip } from 'reactstrap';
import { translate } from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { actionSetEditAsset, actionDeleteAsset, actionGetAssetTypes } from 'store/actions/assetsActions';
import { actionGetKitTypes } from 'store/actions/kitsActions';
import SweetAlert from 'react-bootstrap-sweetalert';
import { actionSweetAlert } from 'store/actions/commonActions';
import { toast } from 'react-toastify';
import moment from 'moment';

const STATUS_TONE = {
  a_st_available:    'success',
  a_st_reserved:     'info',
  a_st_assigned:     'primary',
  a_st_unavailable:  'secondary',
  a_st_deleted:      'danger',
};

const TYPE_ICON = {
  cellphone:  'now-ui-icons tech_mobile',
  device:     'now-ui-icons objects_diamond',
  'sim card': 'now-ui-icons tech_tv',
};

const TableAssets = (props) => {
  const { data, page, callback, t } = props;
  const dispatch   = useDispatch();
  const history    = useHistory();
  const kitTypes   = useSelector((s) => s.kitsReducer.types);
  const assetTypes = useSelector((s) => s.assetsReducer.types);

  useEffect(() => {
    if (!Object.keys(kitTypes).length) dispatch(actionGetKitTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
      if (!Object.keys(assetTypes).length) dispatch(actionGetAssetTypes());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const editAsset = (e, asset) => {
    e.preventDefault();
    dispatch(actionSetEditAsset({ ...asset }));
    history.push(`/admin/assets/form/${asset._id}`);
  };

  const copyId = (e, val) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(val);
    toast.success(t('id-copied') || 'Copied');
  };

  const deleteAsset = (e, asset) => {
    e.preventDefault();
    dispatch(
      actionSweetAlert(
        <SweetAlert
          warning
          title={`${t('confirm-delete-title') || 'Delete asset'}: ${asset.asset_type} (${asset.brand})?`}
          onConfirm={() => {
            dispatch(actionDeleteAsset(asset._id));
            if (data.length === 1 && page > 0) callback(page - 1);
            dispatch(actionSweetAlert(null));
          }}
          onCancel={() => dispatch(actionSweetAlert(null))}
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="info"
          confirmBtnText={t('confirm-delete-yes') || 'Yes, delete it'}
          cancelBtnText={t('cancel-btn') || 'Cancel'}
          showCancel
        >
          {t('confirm-delete-warning') || 'All the data related to it will be lost.'}
        </SweetAlert>
      )
    );
  };

  return (
    <div className="kit-table-wrap">
      <table className="kit-table">
        <thead>
          <tr>
            <th style={{ width: '160px' }}>{t('status')}</th>
            <th style={{ width: '170px' }}>{t('identifier')}</th>
            <th>{t('type')}</th>
            <th>{t('brand')}</th>
            <th>{t('model')}</th>
            <th>{t('serial')}</th>
            <th style={{ width: '160px' }}>{t('created')}</th>
            <th style={{ width: '110px', textAlign: 'right' }}>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p, idx) => {
            const tone = STATUS_TONE[p.status] || 'secondary';
            const icon = TYPE_ICON[p.asset_type] || 'now-ui-icons objects_diamond';
            return (
              <tr key={p._id || idx} className={`kit-row tone-${tone}`}>
                <td>
                  <Badge color={tone} pill className="kit-status-badge">{t(p.status)}</Badge>
                </td>
                <td>
                  {p.identifier ? (
                    <div className="kit-id-cell">
                      <span className="kit-id-code">{p.identifier}</span>
                      <button
                        type="button"
                        className="kit-id-copy"
                        onClick={(e) => copyId(e, p.identifier)}
                        id={`as-copy-${idx}`}
                        aria-label="Copy"
                      >
                        <i className="now-ui-icons files_single-copy-04" />
                      </button>
                      <UncontrolledTooltip target={`as-copy-${idx}`}>{p.identifier}</UncontrolledTooltip>
                    </div>
                  ) : <span className="kit-cell-muted">—</span>}
                </td>
                <td>
                  <div className="asset-type-cell">
                    <i className={`asset-type-icon ${icon}`} />
                    <div>
                      <div className="asset-type-name">{p.asset_type || '—'}</div>
                      {p.asset_type === 'device' && p.study_type && (
                        <div className="asset-type-study">{p.study_type}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{p.brand || <span className="kit-cell-muted">—</span>}</td>
                <td>{p.model || <span className="kit-cell-muted">—</span>}</td>
                <td>{p.serial_number || <span className="kit-cell-muted">—</span>}</td>
                <td>
                  {p.created_at ? (
                    <time className="kit-status-time">
                      {moment(p.created_at).format('DD-MM-YYYY HH:mm:ss')}
                    </time>
                  ) : '—'}
                </td>
                <td className="kit-actions-cell">
                  {p.status !== 'a_st_deleted' && p.is_editable && (
                    <div className="kit-actions">
                      <button
                        type="button"
                        className="kit-action-btn"
                        onClick={(e) => editAsset(e, p)}
                        id={`as-edit-${idx}`}
                      >
                        <i className="fa fa-edit" />
                      </button>
                      <UncontrolledTooltip target={`as-edit-${idx}`}>{t('edit-btn') || 'Edit'}</UncontrolledTooltip>

                      <button
                        type="button"
                        className="kit-action-btn kit-action-danger"
                        onClick={(e) => deleteAsset(e, p)}
                        id={`as-del-${idx}`}
                      >
                        <i className="fa fa-trash" />
                      </button>
                      <UncontrolledTooltip target={`as-del-${idx}`}>{t('delete-btn') || 'Delete'}</UncontrolledTooltip>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

TableAssets.defaultProps = { data: [], loading: true };
TableAssets.propTypes    = { data: PropTypes.array, loading: PropTypes.bool };

export default translate('assets')(TableAssets);
